import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    MAT_DIALOG_DATA, MatDialogModule, MatDialogRef
} from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { CONSTANTS } from '../../../../Constants/Constants';
import { ICostReviewRequestCCRecipient } from '../../../../models/common/ICostReviewRequestCCRecipient';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { CostReviewerPreferences } from '../../../../models/dtos';
import { CommonDataService } from '../../../../services/common-data.service';
import { GetReviewDetailsService } from '../../../../services/api/get-review-details.service';
import { takeUntil } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';

@Component({
    selector: 'app-cost-review-add-cc-recipient',
    imports: [
        TranslatePipe,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        CloseOnEscapeDirective
    ],
    templateUrl: './cost-review-add-cc-recipient.html',
    styleUrl: './cost-review-add-cc-recipient.scss',
})
export class CostReviewAddCcRecipient implements OnInit, OnDestroy {
    isEmailVerified: boolean = false;
    availableRecipients: ICostReviewRequestCCRecipient[] = [];
    private costReviewerCcPrefs: CostReviewerPreferences | null = null;
    private readonly destroy$ = new Subject<void>();
    isCostReviewRequest: boolean = false;
    private existingEmails: string[] = [];

    addCcRecipientForm: FormGroup<{
        available: FormControl<string>;
        email: FormControl<string>;
    }> = new FormGroup({
        available: new FormControl(CONSTANTS.EMPTY_STRING),
        email: new FormControl(CONSTANTS.EMPTY_STRING, [Validators.required , Validators.email])
    });

    constructor(
        private readonly dialogRef: MatDialogRef<CostReviewAddCcRecipient>,
        private readonly notificationMessageService: NotificationMessageService,
        private readonly translate: TranslateService,
        private readonly commonDataService: CommonDataService,
        private readonly getReviewDetailsService: GetReviewDetailsService,
        @Inject(MAT_DIALOG_DATA)
        private readonly data: {
            dataSourceCCRecipient?: ICostReviewRequestCCRecipient[];
            isCostReviewRequest: boolean;
        }
    ) { }

    ngOnInit(): void {
        const userPreferences = this.commonDataService.getPreferenceData();
        this.costReviewerCcPrefs = userPreferences.costReviewerPreferences;
        this.availableRecipients = (this.costReviewerCcPrefs?.crAvailableCCUsers ?? []).map(email => ({ email }));
        this.isCostReviewRequest = this.data.isCostReviewRequest;

        this.existingEmails = this.isCostReviewRequest
            ? (this.data.dataSourceCCRecipient ?? []).map(r => r.email.toLowerCase())
            : [
                ...(this.costReviewerCcPrefs?.crEmailCCUsers ?? []),
                ...(this.costReviewerCcPrefs?.crAvailableCCUsers ?? [])
              ].map(e => e.toLowerCase());

        this.addCcRecipientForm.get('email')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isEmailVerified = false;
        });

    }

    get email(): string {
        return this.addCcRecipientForm.controls.email.value;
    }

    get isVerifyDisabled(): boolean {
        const emailControl = this.addCcRecipientForm.controls.email;
        return this.isEmailVerified || !emailControl?.dirty || emailControl.invalid;
    }

    onAvailableRecipientChange(event: MatSelectChange): void {
        this.addCcRecipientForm.controls.email.setValue(event.value);
        this.isEmailVerified = true;
    }

    onVerifyClick(): void {
        if (this.email && !this.email.endsWith(CONSTANTS.SIEMENS_EMAIL_DOMAIN)) {
            const errorMessage = this.translate.instant('INVALID_EMAIL_FORMAT');
            this.notificationMessageService.showWarning(errorMessage);
            this.isEmailVerified = false;
            return;
        }

        this.isEmailVerified = true;
    }
    

    onOkClick(): void {
        const emailLower = this.email.toLowerCase();
        const alreadyExists = this.existingEmails.includes(emailLower);

        if (alreadyExists) {
            const errorMessage = this.translate.instant('COST_REVIEWER_EXISTS');
            this.notificationMessageService.showError(errorMessage);
            return;
        }
        this.dialogRef.close(this.email);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}